// AboutPage: Info about the event platform
import React, { useEffect, useState } from 'react';
import PublicLayout from '../layouts/PublicLayout';
import { Box, Typography, CircularProgress, Button, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import api from '../services/api';
import { getCMS } from '../services/cms';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

function AboutPage() {
  const [aboutContent, setAboutContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const [bgMap, setBgMap] = useState({});

  useEffect(() => {
    setLoading(true);
    api.get('/api/cms?types=about')
      .then(res => {
        const items = Array.isArray(res.data) ? res.data.filter(i => i.type === 'about') : [];
        if (items.length > 0) {
          items.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
          setAboutContent(items[0].contentJSON || null);
        } else {
          setAboutContent(null);
        }
      })
      .catch(() => setAboutContent(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let mounted = true;
    getCMS().then(items => {
      if (!mounted) return;
      const landing = (items || []).find(i => i.type === 'landing');
      setBgMap(landing?.contentJSON?.backgrounds || {});
    }).catch(() => { }).finally(() => { mounted = false; });
  }, []);

  return (
    <PublicLayout>
      <Box sx={{ position: 'relative', width: '100%', minHeight: '60vh', py: { xs: 6, md: 10 }, overflow: 'hidden' }}>
        {bgMap.about && (
          <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${bgMap.about})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', zIndex: 0 }} />
        )}
        {bgMap.about && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.12)', zIndex: 0 }} />
        )}

        <Box sx={{ maxWidth: 1100, mx: 'auto', position: 'relative', zIndex: 1, p: { xs: 2, md: 4 } }}>
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 4, boxShadow: 6, p: { xs: 2, md: 4 } }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom color="primary" sx={{ textAlign: 'center', mb: 3, letterSpacing: 1 }}>
              {aboutContent?.heading || 'About Us'}
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
            ) : aboutContent ? (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 4 }}>
                {aboutContent.image && (
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <img src={aboutContent.image} alt="About" style={{ width: '100%', maxWidth: 340, borderRadius: 12, boxShadow: 3, marginBottom: 24 }} />
                  </Box>
                )}
                <Box sx={{ flex: 2 }}>
                  <Typography variant="body1" color="text.primary" sx={{ mb: 2, fontWeight: 400, letterSpacing: 0.2 }}>
                    {aboutContent.text || 'About the Expo & Conference Platform. Professional event management for all your needs.'}
                  </Typography>
                  {aboutContent.link && (
                    <Button href={aboutContent.link} target="_blank" rel="noopener" variant="contained" color="secondary" sx={{ mt: 2, fontWeight: 700, borderRadius: 2 }}>
                      Learn More
                    </Button>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 4 }}>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img src="/assets/about.jpg" alt="About" style={{ width: '100%', maxWidth: 340, borderRadius: 12, boxShadow: 3, marginBottom: 24 }} />
                </Box>
                <Box sx={{ flex: 2 }}>
                  <Typography variant="body1" color="text.primary" sx={{ mb: 2, fontWeight: 400, letterSpacing: 0.2 }}>
                    About the Expo & Conference Platform. Professional event management for all your needs.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Static Expo Details (preserve backend About content above) */}
          <Box sx={{ maxWidth: 1100, mx: 'auto', mt: { xs: 3, md: 5 }, p: { xs: 2, md: 4 }, width: '100%' }}>
            <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, boxShadow: 3, p: { xs: 2, md: 4 }, overflow: 'hidden' }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ textAlign: 'left', mb: 2, color: 'primary.main' }}>
                Expo Theme
              </Typography>
              <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.primary', mb: 1 }}>
                “Transforming Healthcare Through Medical Tourism: Innovation, Collaboration, and Access”
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.7 }}>
                This theme aligns directly with President Samia Suluhu Hassan’s healthcare vision, emphasizing improved quality of care, greater accessibility for citizens, and enhanced global competitiveness of Tanzania’s health sector.
              </Typography>

              <Grid container spacing={4} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>Objectives</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.7 }}>
                    The main objective of the Tanzania International Medical Tourism Expo is to position Tanzania as a leading destination for medical tourism. This will be achieved by showcasing national healthcare capabilities, fostering international collaborations,+                    and educating the public about available specialized healthcare services within the country.
                  </Typography>

                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>Daily Themes & Activities</Typography>
                  <List disablePadding>
                    {[
                      'Exhibitions showcasing advanced medical services and technologies.',
                      'Seminars and workshops led by global healthcare experts.',
                      'Parallel sessions and interactive engagements designed to educate and connect participants.'
                    ].map((txt, i) => (
                      <ListItem key={i} sx={{ alignItems: 'flex-start', p: 0, mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.2 }}>
                          <CheckCircleIcon color="primary" sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText primary={txt} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', lineHeight: 1.6 }} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>Key Features for Attendees</Typography>
                  <List disablePadding>
                    {[
                      'Free health screenings for attendees.',
                      'Networking sessions for investors and healthcare professionals.',
                      'Live demonstrations of medical procedures and healthcare innovations.',
                      'International hospital partnerships focused on technology transfer.'
                    ].map((txt, i) => (
                      <ListItem key={i} sx={{ alignItems: 'flex-start', p: 0, mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.2 }}>
                          <CheckCircleIcon color="primary" sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText primary={txt} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', lineHeight: 1.6 }} />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ height: 1, bgcolor: 'divider', my: 2 }} />

                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>Topics & Seminar Subtopics</Typography>
                  <List disablePadding>
                    {[
                      'Investing in health infrastructure in Tanzania.',
                      'Innovation and technology in the health sector.'
                    ].map((txt, i) => (
                      <ListItem key={i} sx={{ alignItems: 'flex-start', p: 0, mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.2 }}>
                          <CheckCircleIcon color="primary" sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText primary={txt} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', lineHeight: 1.6 }} />
                      </ListItem>
                    ))}
                  </List>

                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, lineHeight: 1.7 }}>
                    These discussions aim to attract investors, encourage innovation, and strengthen the healthcare ecosystem.
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </PublicLayout>
  );
}

export default AboutPage;
